/** Большое окно похожих на выбранную вещь: слева фото и что о ней известно, справа `SerialModels`
 * от модели или серии (кэш векторов `dress_model_similar`) с наличием в городе; стрелки (и ← →) —
 * соседние вещи той же выдачи, Esc — закрыть. Открывают «Товар» (карточки «понравилось») и сердечки
 * карточки прихода в ленте (`VisitCard`). */
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { useEffect } from "react";

import { SerialModels } from "@/components/chat/SerialModels";
import type { OnOffer } from "@/components/chat/SerialPhotos";
import type { ModelsOf } from "@/lib/serialModels";

export type SimilarItem = {
  key: string | number;
  photo: string | null;
  /** От чего искать; `null` — искать не от чего (модели в CRM нет), стрелки такую вещь пропускают. */
  of: ModelsOf | null;
  /** В шапку окна: «Похожие на выбранное · …». */
  title: string | null;
  /** Первая строка — жирным, остальные — мелко. */
  lines: string[];
  note?: string | null;
};

const box = "aspect-[3/4] w-full rounded-[8px] bg-black/5 object-cover";

export function SimilarDialog({
  list,
  at,
  location,
  onMove,
  onClose,
  onOffer,
}: {
  list: SimilarItem[];
  at: number;
  /** Салон сразу; `null` — город решит сервер (у серии — её город). */
  location: number | null;
  onMove: (i: number) => void;
  onClose: () => void;
  onOffer: OnOffer;
}) {
  const c = list[at]!;
  const step = (d: number) => {
    for (let i = at + d; i >= 0 && i < list.length; i += d) if (list[i]!.of && list[i]!.photo) return onMove(i);
  };
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented) return; // клавишу уже взял слайдер фото поверх окна
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") step(-1);
      else if (e.key === "ArrowRight") step(1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });
  const [about, ...from] = c.lines;
  const arrow = "grid size-8 place-items-center rounded-full border border-border hover:bg-bg-subtle disabled:opacity-40";
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-3" onClick={onClose}>
      <div
        className="flex h-full max-h-[92vh] w-full max-w-[1180px] flex-col overflow-hidden rounded-[16px] bg-bg text-[13px] text-fg"
        onClick={(e) => e.stopPropagation()}
        data-annot="товар: окно похожих"
      >
        <div className="flex flex-none items-center gap-2 border-b border-border px-4 py-2.5">
          <div className="min-w-0 flex-1 truncate text-[15px] font-medium">Похожие на выбранное{c.title ? ` · ${c.title}` : ""}</div>
          <span className="text-[12px] text-fg-muted tabular-nums">
            {at + 1} из {list.length}
          </span>
          <button type="button" className={arrow} onClick={() => step(-1)} title="Предыдущее (←)">
            <ChevronLeft size={16} />
          </button>
          <button type="button" className={arrow} onClick={() => step(1)} title="Следующее (→)">
            <ChevronRight size={16} />
          </button>
          <button type="button" onClick={onClose} title="Закрыть (Esc)" className="rounded p-1 text-fg-muted hover:bg-bg-subtle hover:text-fg">
            <X size={18} />
          </button>
        </div>
        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto md:flex-row md:overflow-hidden">
          <aside className="flex flex-none flex-col gap-2 border-border p-4 md:w-[260px] md:overflow-y-auto md:border-r">
            {c.photo && (
              <a href={c.photo} target="_blank" rel="noreferrer" title="Открыть фото" className="relative block">
                <img src={c.photo} alt="" className={`${box} max-md:mx-auto max-md:max-w-[200px]`} />
              </a>
            )}
            {about && <div className="font-medium">{about}</div>}
            {from.map((x, i) => (
              <div key={i} className="text-[12px] text-fg-muted">
                {x}
              </div>
            ))}
            {c.note && <div className="text-[12px] text-fg-muted">{c.note}</div>}
          </aside>
          <div className="min-w-0 flex-1 px-4 md:overflow-y-auto">
            {c.of && (
              <SerialModels
                key={"serial" in c.of ? `s${c.of.serial}` : `m${c.of.model}`}
                of={c.of}
                start={location}
                title="Похожие по фото"
                wide
                onOffer={(text, files) => {
                  onOffer(text, files);
                  onClose();
                }}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
